import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { Card } from '../../../_components'
import Select from 'react-select'

const MomentumWeekDaySelectUnit = ( { data, weekDayLabel, weekDayValue, onWeekDayToggle, ...props } ) => {
  const checked = useMemo( () => {
    return !!data?.worktime?.weekdays?.includes(weekDayValue)
  }, [ data, weekDayValue ] )

  const handleChange = useCallback( () => {
    if ( onWeekDayToggle ) onWeekDayToggle( weekDayValue )
  }, [ onWeekDayToggle, weekDayValue ] )

  return (
    <div {...props}>
      <div className='form-check'>
        <input className="form-check-input" type="checkbox" checked={checked} onChange={handleChange} />
        <label className="form-check-label font-weight-lighter">
          {weekDayLabel}
        </label>
      </div>
    </div>
  )
}

const MomentumWeekDaySelect = ( { data = {}, setData, queryType, errors } ) => {
  const [ timeInterval ] = useState( 10 )
  const setCardInRequired = useMemo( () => {
    if ( !data.worktime ) return false

    const areWeekDays = !!data.worktime.weekdays?.length
    const areStartAt = !!data.worktime.start_at
    const areStopAt = !!data.worktime.stop_at
    const areSomeThing = areWeekDays || areStartAt || areStopAt

    if ( areSomeThing ) return true

    return false
  }, [ data ] )

  const handleWeekDayToggle = useCallback( ( weekDayValue ) => {
    setData( data => {
      const weekdaysNormalizedArray = data?.worktime?.weekdays ?? []
      const checked = weekdaysNormalizedArray.includes( weekDayValue )
      const weekdays = (
        checked
        ? weekdaysNormalizedArray.filter(weekday => weekday !== weekDayValue)
        : weekdaysNormalizedArray.concat(weekDayValue)
      ).sort( ( a, b ) => {
        if ( a > b ) return 1
        if ( b > a ) return -1
        return 0
      } )
      const worktime = { ...data.worktime, weekdays }
      return { ...data, worktime }
    } )
  }, [ setData ] )

  const hoursOptions = useMemo( () => {
    const options = Array( 24 * 60 / timeInterval ).fill( 0 ).map( ( v, index ) => {
      const calc = index * timeInterval / 60
      const hours = Math.trunc( calc ).toString().padStart( 2, '0' )
      const minutes = ( 60 * ( calc % 1 ) ).toFixed( 0 ).padStart( 2, '0' )
      return {
        label: `${hours}:${minutes}`,
        value: `${hours}:${minutes}`
      }
    } )
    return options
  }, [ timeInterval ] )

  const handleHourStart = useCallback( ( event ) => {
    setData( data => {
      const start_at = event?.value
      const worktime = { ...data?.worktime, start_at }

      return { ...data, worktime }
    } )
  }, [ setData ] )

  const handleHourEnd = useCallback( ( event ) => {
    setData( data => {
      const stop_at = event?.value
      const worktime = { ...data?.worktime, stop_at }

      return { ...data, worktime }
    } )
  }, [ setData ] )

  const hasError = useMemo( () => {
    return !!errors.weekDaysWeekDaysError || !!errors.weekDaysStartAtError || !!errors.weekDaysStopAtError
  }, [ errors.weekDaysWeekDaysError, errors.weekDaysStartAtError, errors.weekDaysStopAtError ] )

  const finish = useMemo( () => {
    return data.finish
      ?? data.momentums?.find( ( { type } ) => type === 'finish' )?.values?.[0]
      ?? {}
  }, [ data ] )

  useEffect( () => {
    if ( !data.worktime ) return void 0

    const areWeekDays = !!data.worktime.weekdays?.length
    const areStartAt = !!data.worktime.start_at
    const areStopAt = !!data.worktime.stop_at
    const areSomeThing = areWeekDays || areStartAt || areStopAt

    if ( !areSomeThing ) return void 0
    if ( data.worktime.end_date === finish.value ) return void 0

    setData( data => ( {
      ...data,
      worktime: {
        ...data.worktime,
        end_date: finish.value
      }
    } ) )
  }, [ finish, data.worktime, setData ] )

  return (
    <Card
      title="Cronograma"
      icon="CalendarIcon"
      required={setCardInRequired}
      description={`O Cronograma define os dias de semana e o hórario em que o ${queryType === "campaign" ? "sua Campanha" : "seu Momentum"} vai aparecer.`}
      help={`O Cronograma define os dias de semana e o hórario em que o ${queryType === "campaign" ? "sua Campanha" : "seu Momentum"} vai aparecer.`}
    >
      { ( hasError ) && (
          <div className='text-danger rounded-sm shadow-sm text-white d-flex flex-column w-100'>
            { errors.weekDaysWeekDaysError && (
              <div className='my-1 font-weight-lighter'>* O dia da semana é obrigatório.</div>
            ) }
            { errors.weekDaysStartAtError && (
              <div className='my-1 font-weight-lighter'>* O horário de início é obrigatório.</div>
            ) }
            { errors.weekDaysStopAtError && (
              <div className='my-1 font-weight-lighter'>* O horário de fim é obrigatório.</div>
            ) }
          </div>
        ) }
      <div className='row'>
        <div className='col-6'>
          <div className='row no-gutters'>
            {[ 'Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sabado' ]
              .map((weekDayLabel, weekDayValue) => {
                return <MomentumWeekDaySelectUnit
                  className='col-4'
                  key={`week-day.${weekDayValue}`}
                  data={data}
                  weekDayLabel={weekDayLabel}
                  weekDayValue={weekDayValue}
                  onWeekDayToggle={handleWeekDayToggle}
                />
              })}
          </div>
        </div>
        <div className='col-6'>
          <div className='d-flex flex-row align-items-center'>
            <label className='m-0 font-weight-lighter' style={ { flex: 0.4 } }>Hora início:</label>
            <Select isClearable
              options={hoursOptions}
              onChange={handleHourStart}
              placeholder='Selecione o horário inicial'
              styles={ {
                container: base => ( { ...base, flex: 1 } ),
                menu: base => ( { ...base, zIndex: 999999 } )
              } }
            />
          </div>
          <div className='d-flex flex-row align-items-center'>
            <label className='m-0 font-weight-lighter' style={ { flex: 0.4 } }>Hora fim:</label>
            <Select isClearable
              options={hoursOptions}
              onChange={handleHourEnd}
              placeholder='Selecione o horário final'
              styles={ {
                container: base => ( { ...base, flex: 1 } ),
                menu: base => ( { ...base, zIndex: 999999 } )
              } }
            />
          </div>
        </div>
      </div>
    </Card>
  )
}

export { MomentumWeekDaySelect }
